/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-02 18:10:54
 **/

import { atom, useAtom, useAtomValue } from "jotai";

import { JotaiStore } from "@/frameworks/libraries/commons";

import { PhoneCallAtoms } from "@/frameworks/core/hooks";
import { Configure } from "@/frameworks/libraries/utilities";
import { usePersistActions } from "@/frameworks/core/persistence";

const LOGTAG = "#useStateAPI#";

const Callees = atom<WebServices.User.UserInfo[]>([]);
const CallID = atom<string>("");

export default function useStateAPI() {
	const dispatch = usePersistActions();
	const _callees = useAtom(Callees, { store: JotaiStore });
	const busyUsers = useAtomValue(PhoneCallAtoms.BusyUsers);

	const add_or_update_callee = async (userID: string) => {
		const user = dispatch.users.get_user(userID);
		// console.debug(LOGTAG, "add_or_update_callee:", user);
		user &&
			_callees[1]((prev) => {
				const index = prev.findIndex((user) => user.userID === userID);
				if (index > -1) {
					prev[index] = user as WebServices.User.UserInfo;
				} else {
					prev = prev.concat(user as WebServices.User.UserInfo);
				}
				return prev;
			});
		return user;
	};

	const reset_callees = async (userID: string) => {
		const user = dispatch.users.get_user(userID);
		user && _callees[1]([user as WebServices.User.UserInfo]);
	};

	const participatedUserList = (callID: string) => {
		const callUsers = useAtomValue(PhoneCallAtoms.CallUsers(callID));
		const callStatus = useAtomValue(PhoneCallAtoms.CallStatus(callID));
		return callUsers
			? callStatus === "ONGOING" || callStatus === "END"
				? Object.values(callUsers)
						.filter(
							(user) =>
								![
									"HANGUP",
									"ENDED",
									"HANGUP_NO_RESPONSE",
									"REJECTED",
									"ENDED_BY_DEVICE_DISCONNECTED"
								].includes(user.status)
						)
						.map((user) => user.userId ?? "")
				: Object.values(callUsers).map((user) => user.userId ?? "")
			: _callees[0].map((user) => user?.userID);
	};

	const activeCallID = Object.keys(useAtomValue(PhoneCallAtoms.CallList))[0];
	const activeCallState = useAtomValue(PhoneCallAtoms.CallUsers(activeCallID));
	const myID = Configure.userID;
	const myCallJoinState = activeCallState ? activeCallState[myID ?? ""] : null;

	return {
		id: useAtom(CallID, { store: JotaiStore }),
		callees: _callees,
		add_or_update_callee,
		reset_callees,
		call: (callID: string) => useAtom(PhoneCallAtoms.Call(callID), { store: JotaiStore }),
		status: (callID: string) => useAtom(PhoneCallAtoms.CallStatus(callID), { store: JotaiStore }),
		time: (callID: string) => useAtom(PhoneCallAtoms.CallTime(callID), { store: JotaiStore }),
		users: (callID: string) => useAtom(PhoneCallAtoms.CallUsers(callID), { store: JotaiStore }),
		participatedUserList: (callID: string) => participatedUserList(callID),
		busyUsers: busyUsers,

		remote_stream: (userID: string) => useAtom(PhoneCallAtoms.RemoteStream(userID), { store: JotaiStore }),
		remote_screen_stream: useAtom(PhoneCallAtoms.RemoteScreenStream, { store: JotaiStore }),

		phone_detail: useAtom(PhoneCallAtoms.PhoneDetail, { store: JotaiStore }),

		active: {
			activeCallState,
			myCallJoinState,
			activeCallID
		}
	};
}
